import React from 'react'
import PropTypes from 'prop-types'
import { default as dayjs } from 'dayjs'
import invalid from '../../../assets/images/invalid.svg'

/**
 * InputField component renders a form input field with optional error handling and default value.
 * It supports various input types and configurations based on the passed props.
 *
 * @module form/InputField
 * @description InputField component renders a form input field with optional error handling and default value.
 * It supports various input types and configurations based on the passed props.
 * @param {Object} field - Configuration for the input field, including type, name, placeholder, etc.
 * @param {Object} errors - Validation errors object, typically provided by react-hook-form.
 * @param {Function} register - Function to register the input field, typically provided by react-hook-form.
 * @param {boolean} isAdd - Boolean flag indicating if the form is in "add" mode or "edit" mode.
 * @param {Object} defaultData - Default data object containing values to prefill the form fields when in "edit" mode.
 *
 * @example
 * const field = {
 *   name: 'username',
 *   type: 'text',
 *   placeholder: 'Enter your username',
 *   size: 6,
 *   defaultValue: 'JohnDoe',
 *   disabled: false,
 *   hidden: false,
 * };
 * const errors = {};
 * const register = () => {};
 * const isAdd = true;
 * const defaultData = {};
 *
 * <InputField field={field} errors={errors} register={register} isAdd={isAdd} defaultData={defaultData} />
 *
 * @returns {React.Element} The rendered InputField component.
 */
export const InputField = ({ field, errors, register, isAdd, defaultData }) => {
  let defDate = !isAdd && defaultData[field.name]
  let defVal

  if (field.defaultValue) {
    defVal = field.defaultValue
  }

  if (!isAdd) {
    if (field.valFromObj) {
      defVal =
        field.obj.length > 1 ? defaultData[field.obj[0]][field.obj[1]] : defaultData[field.obj[0]]
    } else {
      defVal = defaultData[field.name]
    }
  }

  if (!isAdd && field.type === 'date') {
    defDate = dayjs(defDate).format('YYYY-MM-DD')
    defDate = defDate.toString()
  }

  try {
    return (
      <div
        className={`trtui-col-md-${field.size}`}
        style={{ display: field.hidden ? 'none' : 'block' }}
      >
        <label className="trtui-form-label">
          {field.placeholderName ? defaultData[field.placeholderNameField] : field.placeholder}
        </label>
        {isAdd ? (
          <>
            {field.defaultValue ? (
              <input
                type={field.type}
                className={`trtui-form-control ${errors[field.name] && 'trtui-is-invalid'}`}
                style={{
                  color: '#000000',
                  paddingRight: 'calc(1.5em + 1rem)',
                  backgroundImage: errors[field.name] && `url("${invalid}")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right calc(0.375em + 0.25rem) center',
                  backgroundSize: 'calc(0.75em + 0.5rem) calc(0.75em + 0.5rem)',
                }}
                placeholder={field.placeholder}
                {...register(field.name)}
                disabled={field.disabled}
                defaultValue={defVal}
              />
            ) : (
              <input
                type={field.type}
                className={`trtui-form-control ${errors[field.name] && 'trtui-is-invalid'}`}
                style={{
                  color: '#000000',
                  paddingRight: 'calc(1.5em + 1rem)',
                  backgroundImage: errors[field.name] && `url("${invalid}")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right calc(0.375em + 0.25rem) center',
                  backgroundSize: 'calc(0.75em + 0.5rem) calc(0.75em + 0.5rem)',
                }}
                placeholder={field.placeholder}
                {...register(field.name)}
                disabled={field.disabled}
              />
            )}

            {errors[field.name] && (
              <div className="trtui-invalid-feedback">{errors[field.name].message}</div>
            )}
          </>
        ) : (
          <>
            <div className="trtui-input-group trtui-has-validation">
              {field.hasPrefix && (
                <span className="trtui-input-group-text">{defaultData[field.prefixKey]}</span>
              )}
              <input
                type={field.type}
                className={`trtui-form-control ${errors[field.name] && 'trtui-is-invalid'}`}
                style={{
                  color: '#000000',
                  paddingRight: 'calc(1.5em + 1rem)',
                  backgroundImage: errors[field.name] && `url("${invalid}")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right calc(0.375em + 0.25rem) center',
                  backgroundSize: 'calc(0.75em + 0.5rem) calc(0.75em + 0.5rem)',
                }}
                placeholder={field.placeholder}
                {...register(field.name)}
                defaultValue={field.noValueDisplay ? '' : defDate || defVal}
                autoComplete={field.noAutocomplete ? 'new-password' : ''}
                disabled={field.disabled}
              />
              {errors[field.name] && (
                <div className="trtui-invalid-feedback">{errors[field.name].message}</div>
              )}
            </div>
          </>
        )}
      </div>
    )
  } catch (err) {
    return <div>Error</div>
  }
}

InputField.propTypes = {
  /**
   * Configuration object for the input field.
   */
  field: PropTypes.object.isRequired,

  /**
   * Default data object for pre-filling the form fields when in edit mode.
   */
  defaultData: PropTypes.object.isRequired,

  /**
   * Validation errors object, typically provided by react-hook-form.
   */
  errors: PropTypes.object.isRequired,

  /**
   * Boolean indicating whether the form is in "add" mode.
   */
  isAdd: PropTypes.bool.isRequired,

  /**
   * Function to register the input field, typically provided by react-hook-form.
   */
  register: PropTypes.func.isRequired,
}
